import { useEffect, useLayoutEffect, useRef, useState } from "react";
import cx from "classnames";
import styles from "./ParticleSpherePreloader.module.css";

const PARTICLE_COUNT = 900;
const SPHERE_RADIUS = 110;
const PERSPECTIVE = 520;

const T_BREATHE = 1600;
const SHOW_DELAY_MS = 150;

type Particle = {
  cx: number;
  cy: number;
  cz: number;
  ox: number;
  oy: number;
  oz: number;
  size: number;
  phase: number;
  speed: number;
};

const fibSphere = (n: number, r: number) => {
  const pts: { x: number; y: number; z: number }[] = [];
  const golden = Math.PI * (3 - Math.sqrt(5));
  for (let i = 0; i < n; i++) {
    const y = 1 - (i / (n - 1)) * 2;
    const rad = Math.sqrt(1 - y * y);
    const theta = golden * i;
    pts.push({
      x: Math.cos(theta) * rad * r,
      y: y * r,
      z: Math.sin(theta) * rad * r,
    });
  }
  return pts;
};

const buildParticles = (): Particle[] => {
  const targets = fibSphere(PARTICLE_COUNT, SPHERE_RADIUS);
  const particles: Particle[] = [];
  for (let i = 0; i < PARTICLE_COUNT; i++) {
    const angle = Math.random() * Math.PI * 2;
    const elev = (Math.random() - 0.5) * Math.PI;
    const dist = SPHERE_RADIUS * (0.2 + Math.random() * 0.5);
    particles.push({
      cx: targets[i].x,
      cy: targets[i].y,
      cz: targets[i].z,
      ox: Math.cos(angle) * Math.cos(elev) * dist,
      oy: Math.sin(elev) * dist,
      oz: Math.sin(angle) * Math.cos(elev) * dist,
      size: 0.28 + Math.random() * 0.32,
      phase: Math.random() * Math.PI * 2,
      speed: 0.5 + Math.random() * 0.9,
    });
  }
  return particles;
};

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

const getGather = (time: number) => {
  const ct = time % T_BREATHE;
  return 1 - ((1 - Math.cos((ct / T_BREATHE) * Math.PI * 2)) / 2) * 0.4;
};

type ParticleSpherePreloaderProps = {
  visible: boolean;
  /**
   * 'fullscreen' (default) — `position: fixed, inset: 0` с секондари-фоном
   * (используется как оверлей всего приложения).
   * 'contained' — `position: absolute` без фона (для встраивания в карточку,
   * родитель должен быть `position: relative` / `isolation: isolate`).
   */
  variant?: 'fullscreen' | 'contained';
  /**
   * Skip the 150 ms delayed-show gate AND disable the 400 ms opacity
   * transition. Used by App-level scene-state restore path: the user is
   * returning to an already-loaded scene, so the sphere needs to appear
   * instantly (no flash of «empty»), display for a fixed window, then
   * fade out normally. Without this, with a 300 ms restore window the
   * sphere never reaches full opacity (150 ms delay + partial fade-in =
   * ~37 % opacity peak before fade-out).
   */
  instant?: boolean;
};

export const ParticleSpherePreloader = ({
  visible,
  variant = 'fullscreen',
  instant = false,
}: ParticleSpherePreloaderProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const stateRef = useRef({
    particles: null as Particle[] | null,
    rotY: 0,
    lastTime: 0,
    elapsed: 0,
    rafId: 0,
  });

  // Delayed-show: если visible держится меньше SHOW_DELAY_MS — сфера не
  // успевает показаться и не мигает. При cache hit (preview прилетает за
  // десятки мс) на экране ничего лишнего; при холодной загрузке сфера
  // плавно проявится через SHOW_DELAY_MS.
  // `instant` пропускает задержку (см. JSDoc) — используется в App.tsx
  // на пути восстановления сцены из snapshot.
  //
  // **`useLayoutEffect`, а не `useEffect`** — критично для `instant` пути.
  // Обычный useEffect запускается ПОСЛЕ первого paint: первый кадр
  // отрисовывается с `delayedVisible=false` (initial state) → класс
  // `.hidden` → opacity:0 → сфера прозрачна, и пользователь видит canvas
  // под ней (preview-модель или fade-in сцены). useLayoutEffect ставит
  // `delayedVisible=true` синхронно ДО paint — первый видимый кадр уже с
  // непрозрачной сферой. Для не-instant пути таймер всё равно fires
  // асинхронно после paint, но это исходный замысел: задержка нужна
  // именно чтобы избежать flash при cache hit на cold-load пути.
  const [delayedVisible, setDelayedVisible] = useState(false);
  useLayoutEffect(() => {
    if (!visible) {
      setDelayedVisible(false);
      return;
    }
    if (instant) {
      setDelayedVisible(true);
      return;
    }
    const timer = setTimeout(() => setDelayedVisible(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [visible, instant]);

  useEffect(() => {
    if (!delayedVisible) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const state = stateRef.current;
    if (!state.particles) {
      state.particles = buildParticles();
    }
    state.lastTime = 0;
    state.elapsed = 0;

    const resize = () => {
      canvas.width = canvas.offsetWidth * devicePixelRatio;
      canvas.height = canvas.offsetHeight * devicePixelRatio;
    };
    resize();
    window.addEventListener("resize", resize);

    const draw = (ts: number) => {
      if (!state.lastTime) state.lastTime = ts;
      const dt = Math.min(ts - state.lastTime, 50);
      state.lastTime = ts;
      state.elapsed += dt;
      state.rotY += 0.0035;

      const gather = getGather(state.elapsed);
      const W = canvas.width;
      const H = canvas.height;
      ctx.clearRect(0, 0, W, H);
      const centerX = W / 2;
      const centerY = H / 2;
      const dpr = devicePixelRatio;
      const cosY = Math.cos(state.rotY);
      const sinY = Math.sin(state.rotY);
      const isDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
      const col = isDark ? "22,22,22" : "8,8,8";

      const mapped = state.particles!.map((p) => {
        const sx0 = lerp(p.ox, p.cx, gather);
        const sy0 = lerp(p.oy, p.cy, gather);
        const sz0 = lerp(p.oz, p.cz, gather);
        const rx = sx0 * cosY + sz0 * sinY;
        const ry = sy0;
        const rz = -sx0 * sinY + sz0 * cosY;
        const sc = PERSPECTIVE / (PERSPECTIVE + rz);
        const pulse = Math.sin(state.elapsed * 0.001 * p.speed + p.phase) * 0.5 + 0.5;
        const depth = Math.max(
          0,
          Math.min(1, (rz + SPHERE_RADIUS * 1.3) / (SPHERE_RADIUS * 2.6))
        );
        return {
          sx: centerX + rx * sc * dpr,
          sy: centerY + ry * sc * dpr,
          sz: rz,
          sc,
          depth,
          pulse,
          size: p.size,
        };
      });

      mapped.sort((a, b) => a.sz - b.sz);

      for (const p of mapped) {
        const alpha =
          Math.min(1, 0.5 + p.depth * 0.5 + p.pulse * 0.15) * gather;
        ctx.beginPath();
        ctx.arc(p.sx, p.sy, p.size * p.sc * dpr, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${col},${alpha.toFixed(3)})`;
        ctx.fill();
      }

      state.rafId = requestAnimationFrame(draw);
    };

    state.rafId = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(state.rafId);
      window.removeEventListener("resize", resize);
    };
  }, [delayedVisible]);

  return (
    <div
      className={cx(
        styles.overlay,
        variant === 'contained' && styles.contained,
        instant && styles.instant,
        !delayedVisible && styles.hidden,
      )}
    >
      <canvas ref={canvasRef} className={styles.canvas} />
    </div>
  );
};
